// docs/62 — emotes in flight. The module broadcasts one `hero_emote` event row per emote to the
// World; this store keeps the ones still playing (by hero), and which heroes are drawn on screen
// right now. Each screen registers the heroes it draws (`useEmoteHeroes`, or `onScreen` from the
// board), shows the bubble over any of them that is emoting, and the sound plays only when the
// hero is on this player's screen: if you can see it, you hear it (EM5). The cues are the
// server's (variant, gain, pitch, frame delays), so every table hears the same laugh.
import { useEffect } from 'react';
import { create } from 'zustand';
import type { Identity } from 'spacetimedb';
import { tables, type DbConnection, type EventContext } from '../../bindings';
import type { SfxCueRow } from '../../bindings/types';
import { playCue } from '../audio/cues';
import { getConn, onConnected } from '../net/connection';
import { rpc } from '../net/reducers';
import { useSession } from '../stores/session';
import { deviceNow } from '../time';
import { isCompanionId } from '../sim/companion';
import { BARD_CLASS, EMOTE_MS, EMOTES, PET_EMOTE_MS, PET_EMOTES } from './catalogue';

/** A hero a screen draws, as the wheel needs it; a ranger's companion goes by its companion id
 *  (`COMPANION_BIT` set) and has the pet wheel. */
export interface EmoteHero { id: bigint; name: string; mine: boolean }

/** Whether this wheel entry is a ranger's companion (docs/59): it has the pet wheel. */
export const isPet = (heroId: bigint | string): boolean => isCompanionId(Number(heroId));
export interface ActiveEmote { seq: number; emote: number; at: number }
export interface WheelState { mode: 'hold' | 'click'; heroId: string }

/** The least time between two of my emotes (the module's `EMOTE_GAP_MS`). */
export const EMOTE_GAP_MS = 600;

interface EmoteStore {
  /** Emotes still playing, by hero id (decimal string). */
  active: Record<string, ActiveEmote>;
  /** Pet emotes still wiggling, by companion id (decimal string); `emote` indexes `PET_EMOTES`. */
  pets: Record<string, ActiveEmote>;
  /** Heroes drawn on screen now, by id, with how many places draw them. */
  onScreen: Record<string, EmoteHero & { n: number }>;
  wheel?: WheelState;
  /** The hero I last emoted with: the wheel starts on them while they are on screen. */
  lastHero?: string;
  sentAt: number;
}

export const useEmotes = create<EmoteStore>()(() => ({ active: {}, pets: {}, onScreen: {}, sentAt: 0 }));

let seq = 1;
let history: Array<{ hero: string; emote: number; heard: boolean }> = [];

/** Shows emote `emote` over hero `heroId` and plays its cues if the hero is on screen. */
export function showEmote(heroId: bigint | string, emote: number, cues: SfxCueRow[] = []): void {
  const key = heroId.toString();
  if (isPet(key)) { showPetEmote(key, emote, cues); return; }
  if (!EMOTES[emote]) return;
  const s = seq++;
  useEmotes.setState((st) => ({ active: { ...st.active, [key]: { seq: s, emote, at: deviceNow() } } }));
  const heard = !!useEmotes.getState().onScreen[key];
  if (heard) for (const c of cues) playCue(c);
  history.push({ hero: key, emote, heard }); if (history.length > 50) history = history.slice(-50);
  setTimeout(() => {
    useEmotes.setState((st) => {
      if (st.active[key]?.seq !== s) return st;
      const active = { ...st.active };
      delete active[key];
      return { active };
    });
  }, EMOTE_MS + 50);
}

/** A companion's pet emote: no bubble, it wiggles (its screens read `pets`) while its sound plays. */
function showPetEmote(key: string, emote: number, cues: SfxCueRow[]): void {
  if (!PET_EMOTES[emote]) return;
  const s = seq++;
  useEmotes.setState((st) => ({ pets: { ...st.pets, [key]: { seq: s, emote, at: deviceNow() } } }));
  const heard = !!useEmotes.getState().onScreen[key];
  if (heard) for (const c of cues) playCue(c);
  history.push({ hero: key, emote, heard }); if (history.length > 50) history = history.slice(-50);
  setTimeout(() => {
    useEmotes.setState((st) => {
      if (st.pets[key]?.seq !== s) return st;
      const pets = { ...st.pets };
      delete pets[key];
      return { pets };
    });
  }, PET_EMOTE_MS + 50);
}

/** Registers heroes as drawn on screen; returns the unregister. */
export function putOnScreen(heroes: EmoteHero[]): () => void {
  if (!heroes.length) return () => {};
  useEmotes.setState((st) => {
    const onScreen = { ...st.onScreen };
    for (const h of heroes) {
      const k = h.id.toString();
      const was = onScreen[k];
      onScreen[k] = { ...h, mine: h.mine || !!was?.mine, n: (was?.n ?? 0) + 1 };
    }
    return { onScreen };
  });
  return () => useEmotes.setState((st) => {
    const onScreen = { ...st.onScreen };
    for (const h of heroes) {
      const k = h.id.toString();
      const was = onScreen[k];
      if (!was) continue;
      if (was.n <= 1) delete onScreen[k]; else onScreen[k] = { ...was, n: was.n - 1 };
    }
    return { onScreen };
  });
}

/** Keeps `heroes` registered as on screen while the calling component is mounted. */
export function useEmoteHeroes(heroes: EmoteHero[]): void {
  const sig = heroes.map((h) => `${h.id}:${h.mine ? 1 : 0}:${h.name}`).join('|');
  // keyed on the line-up's signature, not the array (screens build a new one every render)
  useEffect(() => putOnScreen(heroes), [sig]);
}

/** My heroes on screen now, in id order. */
export function myHeroesOnScreen(st: Pick<EmoteStore, 'onScreen'> = useEmotes.getState()): EmoteHero[] {
  return Object.values(st.onScreen).filter((h) => h.mine).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Is this hero a bard (`hero.class`, docs/104)? A bard's wheel has their own musical emotes. */
export function isBardHero(heroId: bigint | string): boolean {
  if (isPet(heroId)) return false;
  try {
    return getConn()?.db.hero.id.find(BigInt(heroId))?.class === BARD_CLASS;
  } catch {
    return false;
  }
}

/** Sends an emote for one of my heroes (nothing while the gap since my last one runs; a bard's own
 *  emotes only for a bard, as the module checks). */
export function sendEmote(heroId: string, emote: number): boolean {
  const now = deviceNow();
  if (now - useEmotes.getState().sentAt < EMOTE_GAP_MS || !(isPet(heroId) ? PET_EMOTES[emote] : EMOTES[emote])) return false;
  if (!isPet(heroId) && EMOTES[emote]?.bard && !isBardHero(heroId)) return false;
  useEmotes.setState({ sentAt: now, lastHero: heroId });
  void rpc.sendEmote(BigInt(heroId), emote);
  return true;
}

// ---- the World's emote rows -------------------------------------------------------------------

interface EmoteRow { worldId: bigint; heroId: bigint; owner: Identity; emote: number; catalogue: number; cues: SfxCueRow[] }
let sub: { unsubscribe(): void } | undefined;
let unsubs: Array<() => void> = [];
let wiredWorld: bigint | undefined;

function wire(c: DbConnection, worldId: bigint): void {
  sub?.unsubscribe();
  for (const u of unsubs) u();
  unsubs = [];
  sub = c.subscriptionBuilder()
    .onError((_ctx, err?: Error) => console.error('[emote] subscription error', err))
    .subscribe([tables.heroEmote.where((r) => r.worldId.eq(worldId))]);
  const onRow = (_ctx: EventContext, row: EmoteRow) => {
    if (row.worldId !== worldId) return;
    showEmote(row.heroId, row.emote, row.cues);
  };
  c.db.heroEmote.onInsert(onRow);
  unsubs.push(() => c.db.heroEmote.removeOnInsert(onRow));
}

/** Follows the chosen World and shows its emotes. Called once at boot. */
export function startEmotes(): void {
  (globalThis as unknown as Record<string, unknown>)['__fkEmotes'] = {
    history: () => history,
    onScreen: () => Object.values(useEmotes.getState().onScreen).map((h) => ({ id: h.id.toString(), name: h.name, mine: h.mine })),
    active: () => useEmotes.getState().active,
    pets: () => useEmotes.getState().pets,
    wheel: () => useEmotes.getState().wheel,
  };
  const follow = (worldId: bigint | undefined) => {
    if (worldId === wiredWorld) return;
    wiredWorld = worldId;
    sub?.unsubscribe(); sub = undefined;
    for (const u of unsubs) u();
    unsubs = [];
    if (worldId === undefined) return;
    const c = getConn();
    if (c) wire(c, worldId);
  };
  onConnected((c) => { if (wiredWorld !== undefined) wire(c, wiredWorld); });
  follow(useSession.getState().worldId);
  useSession.subscribe((s) => follow(s.worldId));
}
